import { computed, ref, toValue, watch, type ComputedRef, type MaybeRefOrGetter, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { toast } from 'vue-sonner'
import { useApi } from '~/api'
import { REPORT_RUNS_QUERY_KEY } from '~/api/models/report-service'
import { useAuthStore } from '~/stores/authStore'
import { useReportDownloadsStore } from '~/stores/reportDownloadsStore'
import type { TableReportType } from '~/types/report'
import type { ReportPreviewRequest } from '~/types/report-preview'
import type { ReportPreviewProfile, ReportTarget } from '~/types/report-definition'
import type { ServiceFilters, ServiceRun, ServiceRunFormat, ServiceRunRequest } from '~/types/report-service'
import { downloadBlob, downloadUrl } from '~/utils/common.utils'
import { buildRunArtefactFilename } from '~/utils/reports/export-filename'
import { isPreviewRequestReady } from '~/utils/reports/preview-request'
import { toReportServiceError } from '~/utils/reports/service-errors'
import {
  createMemoisedAssetIdResolver,
  toServiceDefinitionRunRequest,
  toServiceRunRequest
} from '~/utils/reports/service-request'

export interface UseReportRunsOptions {
  reportType?: TableReportType
  request?: ComputedRef<ReportPreviewRequest | undefined>
  limit?: number
  target?: MaybeRefOrGetter<ReportTarget | undefined>
  profile?: MaybeRefOrGetter<ReportPreviewProfile | undefined>
  filters?: MaybeRefOrGetter<ServiceFilters | undefined>
}

export interface UseReportRunsReturn {
  runs: ComputedRef<ServiceRun[]>
  isLoading: ComputedRef<boolean>
  error: ComputedRef<Error | null>
  activeCount: ComputedRef<number>
  isQueueing: Ref<boolean>
  downloading: Ref<string | null>
  canPrepare: ComputedRef<boolean>
  prepareExport: (formats: ServiceRunFormat[]) => Promise<void>
  download: (run: ServiceRun, format: ServiceRunFormat) => Promise<void>
  cancel: (run: ServiceRun) => Promise<void>
  retry: (run: ServiceRun) => Promise<void>
  canCancel: (run: ServiceRun) => boolean
  canRetry: (run: ServiceRun) => boolean
  refresh: () => Promise<void>
}

export const RUNS_PANEL_QUERY_KEY = [REPORT_RUNS_QUERY_KEY, 'panel'] as const

export const REPORT_RUNS_MAX_LIMIT = 100

const ACTIVE_STATUSES: ServiceRun['status'][] = ['queued', 'running']
const TERMINAL_STATUSES: ServiceRun['status'][] = ['done', 'failed']
const FAST_POLL_MS = 3000
const SLOW_POLL_MS = 10000
const SLOW_POLL_AFTER_MS = 2 * 60 * 1000
const DEFAULT_LIMIT = 100

export const isRunActive = (run: ServiceRun): boolean => ACTIVE_STATUSES.includes(run.status)

export const isRunTerminal = (run: ServiceRun): boolean => TERMINAL_STATUSES.includes(run.status)

export const isRunCancellable = (run: ServiceRun): boolean => run.status === 'queued'

export const getRunsPollInterval = (runs: ServiceRun[] | undefined, nowMs: number): number | false => {
  const active = (runs ?? []).filter(isRunActive)
  if (active.length === 0) return false
  const oldest = Math.min(...active.map((run) => new Date(run.requestedAt).getTime()).filter(Number.isFinite))
  return Number.isFinite(oldest) && nowMs - oldest > SLOW_POLL_AFTER_MS ? SLOW_POLL_MS : FAST_POLL_MS
}

export const downloadKey = (run: Pick<ServiceRun, 'id'>, format: ServiceRunFormat): string => `${run.id}:${format}`

export const useReportRuns = (options: UseReportRunsOptions = {}): UseReportRunsReturn => {
  const api = useApi()
  const queryClient = useQueryClient()
  const authStore = useAuthStore()
  const store = useReportDownloadsStore()
  const { t } = useI18n()
  const resolveAssetIds = createMemoisedAssetIdResolver((filter) => api.assets.getFilteredAssetIds(filter))

  const isQueueing = ref(false)
  const downloading = ref<string | null>(null)
  const limit = Math.min(options.limit ?? DEFAULT_LIMIT, REPORT_RUNS_MAX_LIMIT)

  const query = useQuery<ServiceRun[], Error>({
    queryKey: computed(() => [...RUNS_PANEL_QUERY_KEY, authStore.activeLicence?.id ?? null, limit]),
    queryFn: () => api.reportService.listRuns({ limit }),
    staleTime: 0,
    retry: 1,
    refetchInterval: (current) => getRunsPollInterval(current.state.data, Date.now())
  })

  const runs = computed<ServiceRun[]>(() => query.data.value ?? [])
  const activeCount = computed(() => runs.value.filter(isRunActive).length)
  const canPrepare = computed(() => !isQueueing.value && isPreviewRequestReady(options.request?.value))

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: [REPORT_RUNS_QUERY_KEY] })
  }

  const openDownloadsAction = (): { label: string; onClick: () => void } => ({
    label: t('reports.export.openDownloads'),
    onClick: () => store.open()
  })

  const notifyQueued = (): void => {
    toast(t('reports.export.queued'), {
      description: t('reports.export.queuedDescription'),
      action: openDownloadsAction()
    })
  }

  const enqueue = async (request: ServiceRunRequest): Promise<void> => {
    const accepted = await api.reportService.createRun(request)
    store.rememberRequest(accepted.id, request)
    notifyQueued()
    await invalidate()
  }

  const queue = (work: () => Promise<void>): Promise<void> => {
    isQueueing.value = true
    return work()
      .catch((error: unknown) => {
        toast.error(t('reports.export.queueError'), { description: toReportServiceError(error).message })
      })
      .finally(() => {
        isQueueing.value = false
      })
  }

  const buildRunRequest = async (
    current: ReportPreviewRequest,
    formats: ServiceRunFormat[]
  ): Promise<ServiceRunRequest> => {
    const target = toValue(options.target)
    const profile = toValue(options.profile)
    if (target?.kind === 'definition' && profile) {
      const filters = toValue(options.filters)
      return await toServiceDefinitionRunRequest(
        current,
        target.definitionId,
        formats,
        { windowMode: profile.windowMode, scopeDimensions: profile.scopeDimensions },
        { resolveAssetIds, ...(filters ? { filters } : {}) }
      )
    }
    return await toServiceRunRequest(current, formats, { resolveAssetIds })
  }

  const prepareExport = async (formats: ServiceRunFormat[]): Promise<void> => {
    const current = options.request?.value
    if (!current || formats.length === 0 || !canPrepare.value) return
    await queue(async () => enqueue(await buildRunRequest(current, formats)))
  }

  const fetchArtefact = async (run: ServiceRun, format: ServiceRunFormat): Promise<void> => {
    const link = await api.reportService.artefactLink(run.id, format)

    if (!link) {
      downloadBlob(await api.reportService.artefactBlob(run.id, format), buildRunArtefactFilename(run, format))
      return
    }

    downloadUrl(link.url)
  }

  const download = async (run: ServiceRun, format: ServiceRunFormat): Promise<void> => {
    if (downloading.value) return
    downloading.value = downloadKey(run, format)
    await fetchArtefact(run, format)
      .catch((error: unknown) => {
        toast.error(t('reports.downloads.downloadError'), { description: toReportServiceError(error).message })
      })
      .finally(() => {
        downloading.value = null
      })
  }

  const cancel = async (run: ServiceRun): Promise<void> => {
    if (!isRunCancellable(run)) return
    try {
      await api.reportService.cancelRun(run.id)
      store.forgetRequest(run.id)
      await invalidate()
    } catch (error) {
      toast.error(t('reports.downloads.cancelError'), { description: toReportServiceError(error).message })
    }
  }

  const canCancel = (run: ServiceRun): boolean => isRunCancellable(run)

  const canRetry = (run: ServiceRun): boolean => run.status === 'failed' && !!store.requestFor(run.id)

  const retry = async (run: ServiceRun): Promise<void> => {
    const request = store.requestFor(run.id)
    if (!request || isQueueing.value) return
    await queue(() => enqueue(request))
  }

  const refresh = async (): Promise<void> => {
    await query.refetch()
  }

  watch(
    runs,
    (current) => {
      if (!query.data.value) return
      const terminal = current.filter(isRunTerminal)
      const newlyTerminal = store.markTerminal(terminal.map((run) => run.id))
      for (const run of terminal.filter((entry) => newlyTerminal.includes(entry.id))) {
        if (run.status === 'done') {
          toast.success(t('reports.downloads.ready', { name: run.name }), { action: openDownloadsAction() })
        } else {
          toast.error(t('reports.downloads.failed', { name: run.name }), {
            description: run.error ?? undefined,
            action: openDownloadsAction()
          })
        }
      }
    },
    { immediate: true }
  )

  return {
    runs,
    isLoading: computed(() => query.isPending.value && query.isFetching.value),
    error: computed(() => query.error.value),
    activeCount,
    isQueueing,
    downloading,
    canPrepare,
    prepareExport,
    download,
    cancel,
    retry,
    canCancel,
    canRetry,
    refresh
  }
}
